import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { RootState } from 'src/store';
import { useReportsApi } from '../api/ReportsApi';
import { ExportMessageErrorReportsParams } from '../api/types';
import {
  changeFilter,
  resetReports,
  setError,
  setLoading,
  setMessageErrorReports,
  setMessageErrorSummaryReports,
} from '../slices/reportsSlice';
import { ReportsFilter } from '../types';

export const useReportsService = () => {
  const dispatch = useDispatch();
  const { messageErrorReports, messageErrorSummaryReports, filter, loading, error } = useSelector(
    (state: RootState) => state.reports,
  );
  const { getMessageErrorReports, getMessageErrorSummaryReports, exportMessageErrorReports } = useReportsApi();

  // Single load function that takes explicit parameters to avoid dependency issues
  const loadReports = useCallback(
    async (filterParams?: Partial<ReportsFilter>) => {
      try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        // Use provided filter or fallback to current filter
        const currentFilter = { ...filter, ...filterParams };

        // Ensure required parameters are present and format dates right before API call
        const errorReportsParams = {
          startDateInterval: dayjs(currentFilter.startDateInterval),
          endDateInterval: dayjs(currentFilter.endDateInterval),
          breakdownBy: currentFilter.breakdownBy,
          companyId: currentFilter.companyId,
          sort: currentFilter.sort,
          groupBy: currentFilter.groupBy,
          query: currentFilter.query,
        };

        const response = await getMessageErrorReports(errorReportsParams);
        dispatch(setMessageErrorReports(response.data));
      } catch (err) {
        dispatch(
          setError(err instanceof Error ? err.message : 'Failed to load message error reports'),
        );
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, getMessageErrorReports, filter],
  );

  const loadMessageErrorSummaryReports = useCallback(
    async (filterParams: { startDateInterval: string; endDateInterval: string }) => {
      const response = await getMessageErrorSummaryReports({
        startDateInterval: dayjs(filterParams.startDateInterval),
        endDateInterval: dayjs(filterParams.endDateInterval),
      });
      dispatch(setMessageErrorSummaryReports(response.data));
    },
    [dispatch, getMessageErrorSummaryReports],
  );

  const changeReportsFilter = useCallback(
    (newFilter: Partial<ReportsFilter>) => {
      dispatch(changeFilter(newFilter));
    },
    [dispatch],
  );

  const resetReportsData = useCallback(() => {
    dispatch(resetReports());
  }, [dispatch]);

  const downloadMessageErrorReports = useCallback(
    (params: ExportMessageErrorReportsParams) => exportMessageErrorReports(params),
    [exportMessageErrorReports],
  );

  return {
    messageErrorReports,
    messageErrorSummaryReports,
    filter,
    loading,
    error,
    loadReports,
    loadMessageErrorSummaryReports,
    downloadMessageErrorReports,
    changeReportsFilter,
    resetReportsData,
  };
};
